import { ButtonPrimitiveDirective } from '@shared/directives/button-primitive.directive';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { LucideAngularModule, WifiOff, RotateCw } from 'lucide-angular';
import { NetworkStatusService } from '@core/services/network-status.service';

/**
 * Presents and manages the offline view.
 */
@Component({
  selector: 'app-offline',
  imports: [ButtonPrimitiveDirective, LucideAngularModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    .of {
      min-height: 100vh;
      background:
        linear-gradient(rgb(var(--talos-ink-rgb) / 0.08) 1px, transparent 1px),
        linear-gradient(90deg, rgb(var(--talos-ink-rgb) / 0.08) 1px, transparent 1px),
        var(--talos-bg);
      background-size: 32px 32px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px 24px;
    }

    .of-inner {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      max-width: 480px;
      width: 100%;
    }

    .of-logo {
      margin-bottom: 48px;
    }
    .of-logo img {
      height: 36px;
      width: auto;
    }

    .of-icon-wrap {
      width: 88px;
      height: 88px;
      border-radius: 50%;
      background: rgb(var(--talos-accent-rgb) / 0.1);
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 28px;
    }

    .of-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 0.78rem;
      font-weight: 700;
      color: var(--talos-accent);
      letter-spacing: 0.16em;
      text-transform: uppercase;
      margin-bottom: 16px;
    }
    .of-badge-dot {
      width: 6px; height: 6px;
      border-radius: 1px;
      background: var(--talos-accent);
      animation: blink 1.5s ease-in-out infinite;
    }
    @keyframes blink {
      0%, 100% { opacity: 1; }
      50%       { opacity: 0.3; }
    }

    .of-heading {
      font-size: clamp(1.5rem, 4vw, 2rem);
      font-weight: 800;
      color: var(--talos-ink);
      line-height: 1.2;
      letter-spacing: -0.025em;
      margin: 0 0 12px;
    }
    .of-desc {
      font-size: 0.9375rem;
      color: rgb(var(--talos-ink-rgb) / 0.6);
      line-height: 1.7;
      margin: 0 0 36px;
    }

    .of-btn-primary {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: var(--talos-accent);
      color: var(--talos-bg);
      padding: 12px 22px;
      border-radius: 2px;
      font-size: 0.9rem;
      font-weight: 600;
      border: 1px solid var(--talos-accent);
      cursor: pointer;
      transition: background 0.15s ease;
    }
    .of-btn-primary:hover {
      background: var(--talos-accent);
    }
  `],
  template: `
    <div class="of">
      <div class="of-inner">
        <div class="of-logo">
          <img src="assets/images/logo_full.svg" alt="FloorPlan AI" />
        </div>

        <div class="of-icon-wrap">
          <lucide-icon [img]="WifiOffIcon" [size]="36" color="var(--talos-accent)" />
        </div>

        <div class="of-badge">
          <span class="of-badge-dot"></span>
          No connection
        </div>

        <h1 class="of-heading">You're offline</h1>
        <p class="of-desc">
          Check your internet connection. This page will reload automatically
          once you're back online.
        </p>

        <button appButton class="of-btn-primary" (click)="retry()">
          <lucide-icon [img]="RetryIcon" [size]="16" />
          Try Again
        </button>
      </div>
    </div>
  `,
})
export class OfflineComponent {
  private readonly network = inject(NetworkStatusService);

  readonly WifiOffIcon = WifiOff;
  readonly RetryIcon = RotateCw;

  retry(): void {
    if (navigator.onLine) {
      this.network.isOnline.set(true);
    }
  }
}
